import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate, statusColor } from "../format";
import type { ChargerConfig, ChargerConnector, Connector, Label, SessionRecord } from "../types";
import { useToast } from "../toast";
import LabelDialog from "../dialogs/LabelDialog";
import ConnectorDialog, { ConnectorFormData } from "../dialogs/ConnectorDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

export default function ChargerDetail({
  id,
  onBack,
  onOpenSession,
}: {
  id: number;
  onBack: () => void;
  onOpenSession: (sessionId: number) => void;
}) {
  const api = useAuthApi();
  const [config, setConfig] = useState<ChargerConfig | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingLabel, setAddingLabel] = useState(false);
  const [deletingLabel, setDeletingLabel] = useState<Label | null>(null);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [addingConnector, setAddingConnector] = useState(false);
  const [editingConnector, setEditingConnector] = useState<ChargerConnector | null>(null);
  const [deletingConnector, setDeletingConnector] = useState<ChargerConnector | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await api<ChargerConfig>(`${MGMT}/charger-configs/${id}`);
      setConfig(cfg);
      if (cfg) {
        const [s, conns] = await Promise.all([
          api<SessionRecord[]>(`${MGMT}/sessions?charge_point_id=${encodeURIComponent(cfg.charge_point_id)}`).catch(() => null),
          api<Connector[]>(`${MGMT}/charger-configs/${id}/connectors`).catch(() => null),
        ]);
        setSessions(s ?? []);
        setConnectors(conns ?? []);
      }
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  const deleteLabel = async () => {
    if (!config || !deletingLabel) return;
    try {
      await api(`${MGMT}/charger-configs/${config.id}/labels/${deletingLabel.id}`, { method: "DELETE" });
      toast.show("Label removed");
      setDeletingLabel(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const handleAddConnector = async (form: ConnectorFormData) => {
    await api(`${MGMT}/charger-configs/${id}/connector-configs`, {
      method: "POST",
      body: JSON.stringify({
        connector_number: form.connector_number,
        connector_type: form.connector_type,
        max_power_kw: form.max_power_kw !== "" ? form.max_power_kw : null,
      }),
    });
    toast.show("Connector added");
    setAddingConnector(false);
    load();
  };

  const handleEditConnector = async (form: ConnectorFormData) => {
    await api(`${MGMT}/charger-configs/${id}/connector-configs/${editingConnector!.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        connector_number: form.connector_number,
        connector_type: form.connector_type,
        max_power_kw: form.max_power_kw !== "" ? form.max_power_kw : null,
      }),
    });
    toast.show("Connector updated");
    setEditingConnector(null);
    load();
  };

  const handleDeleteConnector = async () => {
    if (!deletingConnector) return;
    try {
      await api(`${MGMT}/charger-configs/${id}/connector-configs/${deletingConnector.id}`, { method: "DELETE" });
      toast.show("Connector removed");
      setDeletingConnector(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (!config) return <Typography color="error">Charger not found</Typography>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flex: 1, fontFamily: "monospace" }}>
          {config.charge_point_id}
        </Typography>
        {config.display_name && (
          <Typography variant="h6" color="text.secondary">{config.display_name}</Typography>
        )}
        <Chip label={config.status} color={statusColor(config.status)} />
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Configuration</Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>Location:</strong> {config.location?.name ?? "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Tariff:</strong> {config.tariff_id ? `#${config.tariff_id}` : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Max Power:</strong> {config.max_power_kw != null ? `${config.max_power_kw} kW` : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Template ID:</strong> {config.config_template_id ?? "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Sync State:</strong> {config.config_sync_state ?? "unknown"}
                </Typography>
                <Typography variant="body2">
                  <strong>Last Config Sync:</strong> {fmtDate(config.last_config_sync_at)}
                </Typography>
                {config.notes && (
                  <Typography variant="body2" color="text.secondary">{config.notes}</Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Hardware Model</Typography>
              {config.charger_model ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>{config.charger_model.vendor}</strong> {config.charger_model.model_name}
                  </Typography>
                  {config.charger_model.connectors && config.charger_model.connectors.length > 0 && (
                    <Typography variant="body2">
                      <strong>Connectors:</strong>{" "}
                      {config.charger_model.connectors
                        .map((c) => `#${c.connector_number} ${c.connector_type}${c.max_power_kw != null ? ` (${c.max_power_kw} kW)` : ""}`)
                        .join(", ")}
                    </Typography>
                  )}
                  {config.charger_model.max_power_kw != null && (
                    <Typography variant="body2">
                      <strong>Max power:</strong> {config.charger_model.max_power_kw} kW
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No model assigned</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Live Connectors */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Live Connectors
          </Typography>
          {connectors.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No connector data available</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Connector #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error Code</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectors.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.connector_id}</TableCell>
                      <TableCell>
                        <Chip label={c.status} size="small" color={statusColor(c.status)} />
                      </TableCell>
                      <TableCell>{c.error_code && c.error_code !== "NoError" ? c.error_code : "—"}</TableCell>
                      <TableCell>{fmtDate(c.updated_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Connector Configuration */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Connector Configuration</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingConnector(true)}>
              Add Connector
            </Button>
          </Stack>
          {(config.charger_connectors ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No connectors configured</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Max Power (kW)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(config.charger_connectors ?? []).map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.connector_number}</TableCell>
                      <TableCell>{c.connector_type}</TableCell>
                      <TableCell align="right">{c.max_power_kw ?? "—"}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => setEditingConnector(c)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeletingConnector(c)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Groups</Typography>
              {(config.charger_groups ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No groups</Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {(config.charger_groups ?? []).map((g) => (
                    <Chip key={g.id} label={g.name} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Labels */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Labels</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingLabel(true)}>
              Add Label
            </Button>
          </Stack>
          {(config.labels ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No labels</Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(config.labels ?? []).map((lbl) => (
                <Chip
                  key={lbl.id}
                  label={`${lbl.key}=${lbl.value}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => setDeletingLabel(lbl)}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Recent Sessions */}
      {sessions.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Recent Sessions
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Session</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell align="right">Energy (kWh)</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.slice(0, 20).map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Button size="small" onClick={() => onOpenSession(s.id)}>
                          #{s.id}
                        </Button>
                      </TableCell>
                      <TableCell>{s.driver_id ? `Driver #${s.driver_id}` : "—"}</TableCell>
                      <TableCell>
                        <Chip label={s.status} size="small" color={statusColor(s.status)} />
                      </TableCell>
                      <TableCell>{fmtDate(s.start_time)}</TableCell>
                      <TableCell>{s.stop_time ? fmtDate(s.stop_time) : "—"}</TableCell>
                      <TableCell align="right">
                        {s.energy_kwh != null ? s.energy_kwh.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell align="right">
                        {s.cost != null ? `${s.cost.toFixed(2)} ${s.currency ?? ""}` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <LabelDialog
        open={addingLabel}
        entityType="charger"
        entityId={config.id}
        onClose={() => setAddingLabel(false)}
        onSaved={() => { setAddingLabel(false); load(); }}
      />

      <ConfirmDialog
        open={deletingLabel !== null}
        title="Remove label?"
        message={`Remove label "${deletingLabel?.key}=${deletingLabel?.value}"?`}
        onCancel={() => setDeletingLabel(null)}
        onConfirm={deleteLabel}
      />

      <ConnectorDialog
        open={addingConnector}
        title="Add Connector"
        onSave={handleAddConnector}
        onClose={() => setAddingConnector(false)}
      />

      <ConnectorDialog
        open={editingConnector !== null}
        title="Edit Connector"
        initial={
          editingConnector
            ? {
                connector_number: editingConnector.connector_number,
                connector_type: editingConnector.connector_type,
                max_power_kw: editingConnector.max_power_kw ?? "",
              }
            : null
        }
        onSave={handleEditConnector}
        onClose={() => setEditingConnector(null)}
      />

      <ConfirmDialog
        open={deletingConnector !== null}
        title="Remove connector?"
        message={`Remove connector #${deletingConnector?.connector_number} (${deletingConnector?.connector_type})?`}
        onCancel={() => setDeletingConnector(null)}
        onConfirm={handleDeleteConnector}
      />
    </>
  );
}
